import type { Entity, Relation, Target } from '../state/domain';
import type { GfxElement } from './core/types';

export type LinkSuggestion = {
  key: string;
  reason: '线索归属' | '同域关联' | '同源共现';
  fromLabel: string;
  toLabel: string;
  /** 墙面元素 id */
  fromNode: string;
  toNode: string;
};

type RefNode = Pick<GfxElement, 'id' | 'refKind' | 'refId'>;

/**
 * 红绳建议：在墙面已有节点之间，找出「应有但尚未建立」的关联。
 * 纯函数，便于测试与复用。
 */
export function suggestLinks(params: {
  wallElements: RefNode[];
  entities: Entity[];
  targets: Target[];
  relations: Relation[];
  limit?: number;
}): LinkSuggestion[] {
  const { wallElements, entities, targets, relations, limit = 12 } = params;

  const byRef = new Map<string, string>();
  for (const el of wallElements) {
    if (el.refKind && el.refId) byRef.set(`${el.refKind}:${el.refId}`, el.id);
  }

  const linked = new Set(relations.map(r => [r.sourceId, r.targetId].sort().join('|')));
  const out: LinkSuggestion[] = [];

  const push = (
    key: string,
    reason: LinkSuggestion['reason'],
    fromKind: 'target' | 'entity',
    fromId: string,
    fromLabel: string,
    toKind: 'target' | 'entity',
    toId: string,
    toLabel: string
  ): void => {
    const fromNode = byRef.get(`${fromKind}:${fromId}`);
    const toNode = byRef.get(`${toKind}:${toId}`);
    if (!fromNode || !toNode || fromNode === toNode) return;
    if (out.some(item => item.key === key)) return;
    out.push({ key, reason, fromLabel, toLabel, fromNode, toNode });
  };

  // 1) 线索未归属到其对象
  for (const entity of entities) {
    if (!entity.targetId) continue;
    if (linked.has([entity.id, entity.targetId].sort().join('|'))) continue;
    const target = targets.find(t => t.id === entity.targetId);
    push(
      `own:${entity.id}:${entity.targetId}`,
      '线索归属',
      'entity',
      entity.id,
      entity.value ?? entity.label ?? entity.type,
      'target',
      entity.targetId,
      target?.name ?? '对象'
    );
  }

  // 2) 邮箱 ↔ 同域域名
  const domains = entities.filter(e => e.type === 'domain');
  for (const email of entities.filter(e => e.type === 'email')) {
    const domain = (email.value ?? email.label ?? '').split('@')[1];
    if (!domain) continue;
    const match = domains.find(d => d.value === domain || d.label === domain);
    if (!match) continue;
    if (linked.has([email.id, match.id].sort().join('|'))) continue;
    push(
      `dom:${email.id}:${match.id}`,
      '同域关联',
      'entity',
      email.id,
      email.value ?? '',
      'entity',
      match.id,
      match.value ?? ''
    );
  }

  // 3) 同一来源共现（已核实线索）
  const bySource = new Map<string, Entity[]>();
  for (const entity of entities) {
    if (entity.status !== 'verified' || !entity.source) continue;
    const list = bySource.get(entity.source) ?? [];
    list.push(entity);
    bySource.set(entity.source, list);
  }
  for (const list of bySource.values()) {
    for (let i = 0; i < list.length - 1; i++) {
      const a = list[i];
      const b = list[i + 1];
      if (linked.has([a.id, b.id].sort().join('|'))) continue;
      push(
        `src:${a.id}:${b.id}`,
        '同源共现',
        'entity',
        a.id,
        a.value ?? a.label ?? a.type,
        'entity',
        b.id,
        b.value ?? b.label ?? b.type
      );
    }
  }

  return out.slice(0, limit);
}

/** 统计墙面上尚未上板（无对应节点）的线索数量。 */
export function unplacedEntities(wallElements: RefNode[], entities: Entity[]): Entity[] {
  const placed = new Set(
    wallElements.filter(el => el.refKind === 'entity' && el.refId).map(el => el.refId as string)
  );
  return entities.filter(entity => !placed.has(entity.id));
}